// ═══════════════════════════════════════════════════════════════
// ContactReportPrintView.jsx
// Path: src/components/reports/ContactReportPrintView.jsx
//
// Global overlay that renders a single contact report as a clean,
// printable, one-page document. Triggered by setting
// printContactReportId in AppContext from any caller (donor detail
// history list, ReportsView table, donor sidebar Donor Notes panel,
// Director Tasks Notebook icon).
//
// Renders nothing when printContactReportId is null. When set:
//   1. Fetches the report by id
//   2. Resolves the donor name from AppContext members
//   3. Resolves the officer name via a one-time profiles query
//   4. Shows a fixed-position white overlay with the formatted report
//   5. Top bar (hidden in print) has Print and Close buttons
//   6. Browser print-to-PDF produces a chrome-free single-page PDF
//
// Phase 6 of CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Printer, X, FileText } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { contactReportsService } from "../../services/contactReportsService";
import { MEETING_TYPES, OPTIONAL_SECTIONS } from "../../constants/contactReportSections";
import { FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ── Print stylesheet (scoped via class names) ───────────────
// Hide the entire app and show only the report body during print.
// `visibility: hidden` preserves layout so children with visibility:
// visible render in their original positions.
const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .crm-print-overlay-content,
  .crm-print-overlay-content * { visibility: visible !important; }
  .crm-print-overlay-content {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    background: #ffffff !important;
    color: #000000 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    max-width: none !important;
    border: none !important;
  }
  .crm-print-overlay-noprint { display: none !important; }
  @page { size: letter; margin: 0.6in; }
}
`;

// ── Helpers ──────────────────────────────────────────────────
const formatLongDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatShortDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const meetingTypeLabel = (val) =>
  MEETING_TYPES.find((t) => t.value === val)?.label || val;

const donorDisplayName = (member) => {
  if (!member) return "Unknown Donor";
  if (member.donorType === "organization") return member.organization || "Organization";
  if (member.donorType === "household") {
    return (
      member.householdName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "Household"
    );
  }
  return (
    member.credentialedFullName ||
    `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
    "Donor"
  );
};

// ── Section block (only renders if content is non-empty) ────
const SectionBlock = ({ title, body }) => {
  const trimmed = (body || "").trim();
  if (!trimmed) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <h3
        style={{
          margin: "0 0 6px",
          fontSize: 11,
          fontWeight: 700,
          color: "#000000",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          borderBottom: "1px solid #000000",
          paddingBottom: 3,
        }}
      >
        {title}
      </h3>
      <div
        style={{
          fontSize: 12,
          color: "#000000",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
      >
        {trimmed}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ContactReportPrintView (global overlay)
// ═══════════════════════════════════════════════════════════════

export const ContactReportPrintView = () => {
  const {
    palette,
    members,
    printContactReportId,
    setPrintContactReportId,
  } = useApp();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [officerName, setOfficerName] = useState(null);

  // ── Fetch report when id changes ───────────────────────────
  useEffect(() => {
    if (!printContactReportId) {
      setReport(null);
      setError(null);
      setOfficerName(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    contactReportsService
      .getById(printContactReportId)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError("Could not load report — " + (err.message || "unknown error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [printContactReportId]);

  // ── Look up officer display name once we have the report ──
  useEffect(() => {
    if (!report?.createdBy) {
      setOfficerName(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("full_name")
      .eq("id", report.createdBy)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        setOfficerName(data?.full_name || null);
      });
    return () => {
      cancelled = true;
    };
  }, [report?.createdBy]);

  // ── Close on Escape key ───────────────────────────────────
  useEffect(() => {
    if (!printContactReportId) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") setPrintContactReportId(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [printContactReportId, setPrintContactReportId]);

  // Render nothing when not active
  if (!printContactReportId) return null;

  const handleClose = () => setPrintContactReportId(null);
  const handlePrint = () => window.print();

  // Resolve donor from local members array
  const donor = report ? members.find((m) => m.id === report.memberId) : null;
  const donorName = donorDisplayName(donor);

  const intel = report?.intelligence || {};

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "32px 16px",
          zIndex: 9999,
          overflowY: "auto",
          fontFamily: FONT_FAMILY,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div
          style={{
            background: "#ffffff",
            maxWidth: 760,
            width: "100%",
            borderRadius: 12,
            boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top bar — hidden in print */}
          <div
            className="crm-print-overlay-noprint"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              background: palette.toolbarBg,
              borderBottom: `1px solid ${palette.cardBorder}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FileText size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: palette.text1,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Donor Contact Report
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: palette.accent1,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                }}
              >
                <Printer size={ICON_SIZES.sm} />
                Print
              </button>
              <button
                type="button"
                onClick={handleClose}
                title="Close (Esc)"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${palette.cardBorder}`,
                  background: "transparent",
                  color: palette.text2,
                  cursor: "pointer",
                }}
              >
                <X size={ICON_SIZES.sm} />
              </button>
            </div>
          </div>

          {/* Loading / error / report body */}
          {loading && (
            <div
              style={{
                padding: 60,
                textAlign: "center",
                color: palette.text3,
                fontSize: 14,
              }}
            >
              Loading…
            </div>
          )}

          {error && (
            <div
              style={{
                padding: 24,
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  color: "#991b1b",
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            </div>
          )}

          {report && !loading && !error && (
            <div
              className="crm-print-overlay-content"
              style={{
                padding: "36px 48px",
                background: "#ffffff",
                color: "#000000",
                fontFamily: FONT_FAMILY,
              }}
            >
              {/* ── Header ─────────────────────────────────── */}
              <div
                style={{
                  borderBottom: "2px solid #000000",
                  paddingBottom: 12,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "#000000",
                  }}
                >
                  Donor Contact Report
                </div>
                <h1
                  style={{
                    margin: "4px 0 6px",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#000000",
                  }}
                >
                  {donorName}
                </h1>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    fontSize: 11,
                    color: "#000000",
                  }}
                >
                  <span>
                    <strong>Visit Date:</strong> {formatLongDate(report.visitDate)}
                  </span>
                  <span>
                    <strong>Meeting Type:</strong>{" "}
                    {meetingTypeLabel(report.meetingType)}
                  </span>
                  <span>
                    <strong>Filed by:</strong>{" "}
                    {officerName || "—"}
                  </span>
                </div>
              </div>

              {/* ── Attendees ─────────────────────────────── */}
              <SectionBlock title="Attendees" body={report.attendees} />

              {/* ── Summary ───────────────────────────────── */}
              <SectionBlock title="Summary" body={report.summary} />

              {/* ── Optional sections (only the non-empty ones) ── */}
              {OPTIONAL_SECTIONS.map((section) => {
                if (section.split) {
                  // Messaging section — render emphasize + avoid as separate blocks
                  const m = intel.messaging || {};
                  return (
                    <React.Fragment key={section.key}>
                      {m.emphasize && (
                        <SectionBlock
                          title="Messaging — Emphasize"
                          body={m.emphasize}
                        />
                      )}
                      {m.avoid && (
                        <SectionBlock
                          title="Messaging — Avoid"
                          body={m.avoid}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                const data = intel[section.key];
                if (!data || !data.notes) return null;
                return (
                  <SectionBlock
                    key={section.key}
                    title={section.label}
                    body={data.notes}
                  />
                );
              })}

              {/* ── Next step (always shown — required field) ── */}
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 12,
                  borderTop: "2px solid #000000",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 16,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#000000",
                        marginBottom: 4,
                      }}
                    >
                      Next Step
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#000000",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {report.nextStep}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#000000",
                        marginBottom: 4,
                      }}
                    >
                      Due
                    </div>
                    <div style={{ fontSize: 13, color: "#000000" }}>
                      {formatShortDate(report.nextStepDate)}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Footer ──────────────────────────────── */}
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 8,
                  borderTop: "1px solid #cccccc",
                  fontSize: 9,
                  color: "#666666",
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                ClearView Philanthropy CRM · Generated{" "}
                {new Date().toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
